import styled, { keyframes } from "styled-components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  height: 100%;
`;

export const CheckoutHeader = styled.div`
  background: var(--surface);
  padding-top: 20px;
  margin-top: 75px;
  text-align: center;

  padding-bottom: 30px;
  h1 {
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 16px;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.8;
  }
  @media (max-width: 920px) {
    margin-top: 66.7px;
  }
`;

export const CheckoutContent = styled.div`
  padding: 60px 40px;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 60px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 1400px) {
    grid-template-columns: 1fr 350px;
    gap: 40px;
    padding: 50px 30px;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 40px 20px;
  }

  @media (max-width: 768px) {
    padding: 30px 15px;
    gap: 30px;
  }
`;

export const CheckoutForm = styled.form`
  background: var(--surface);
  padding: 40px;
  border-radius: 25px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  animation: ${slideInLeft} 1s ease-out;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 30px;
  text-transform: uppercase;
  color: var(--text-primary);
  position: relative;
  padding-bottom: 15px;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--gradient-primary);
  }
`;

export const FormSection = styled.div`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.$columns || "1fr"};
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  label {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    .required {
      color: var(--accent);
    }
  }
`;

export const Input = styled.input`
  padding: 15px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 15px;
  font-size: 16px;
  font-family: "Source Code Pro", monospace;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &:invalid {
    /* border-color: var(--error); */
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    color: var(--text-secondary);
  }
`;

export const SelectFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-family: "Source Code Pro", monospace;
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 40px;
  margin-top: -20px;
  gap: 8px;

  input[type="checkbox"] {
    margin: 0;
    width: 20px;
    height: 20px;

    appearance: none;
    -webkit-appearance: none;
    background-color: transparent;
    border: 2px solid var(--accent);
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;

    &:checked {
      background-color: var(--accent);
      border-color: var(--accent);
    }

    /* Checkmark */
    &:checked::after {
      content: "";
      position: absolute;
      top: 2px;
      left: 6px;
      width: 4px;
      height: 9px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  label {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    letter-spacing: 0.5px;

    .required {
      color: var(--accent);
    }
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: var(--accent);
    letter-spacing: 0.5px;
    cursor: pointer;
    text-decoration: underline;
  }
`;

export const PaymentMethodSelector = styled.div`
  display: grid;
  gap: 15px;
`;

export const PaymentMethod = styled.div`
  border: 2px solid
    ${(props) => (props.$selected ? "var(--accent)" : "var(--accent-light)")};
  border-radius: 15px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--accent);
  }

  .method-header {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 10px;

    .icon {
      font-size: 24px;
      color: var(--accent);
    }

    .info {
      flex: 1;

      h4 {
        font-size: 16px;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 5px;
      }

      p {
        font-size: 14px;
        color: var(--text-secondary);
        margin: 0;
      }
    }

    .radio {
      width: 20px;
      height: 20px;
      border: 2px solid
        ${(props) =>
          props.$selected ? "var(--accent)" : "var(--accent-light)"};
      border-radius: 50%;
      position: relative;
      transition: all 0.3s ease;

      ${(props) =>
        props.$selected &&
        `
        border-color: var(--accent);
        
        &::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--accent);
        }
      `}
    }
  }

  .method-details {
    display: ${(props) => (props.$selected ? "block" : "none")};
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid var(--accent-light);
  }
`;
export const PaymentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const OrderSummary = styled.div`
  background: var(--surface);
  border-radius: 25px;
  padding: 40px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  position: sticky;
  top: 120px;
  height: fit-content;
  animation: ${slideInRight} 1s ease-out 0.3s both;
  transition: all 0.3s ease;

  @media (max-width: 968px) {
    position: static;
    order: -1;
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

export const SummaryHeader = styled.h3`
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 30px;
  color: var(--text-primary);
  text-align: center;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--accent-light);
`;

export const OrderItem = styled.div`
  display: flex;
  gap: 15px;
  padding: 20px 0;
  border-bottom: 1px solid var(--accent-light);

  &:last-child {
    border-bottom: none;
  }

  .item-image {
    width: 80px;
    height: 60px;
    border-radius: 10px;
    border: 2px solid var(--accent-light);
    flex-shrink: 0;
    transition: all 0.3s ease;
    object-fit: cover;
  }

  .item-details {
    flex: 1;

    .item-name {
      font-size: 16px;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 5px;
    }

    .item-description {
      font-size: 14px;
      color: var(--text-secondary);
      margin-bottom: 8px;
      line-height: 1.4;

      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .item-price {
      font-size: 16px;
      font-weight: 700;
      color: var(--accent);
    }
  }
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid var(--accent-light);

  &:last-of-type {
    border-bottom: 2px solid var(--accent-light);
    margin-bottom: 20px;
  }

  &.total-row {
    font-size: 20px;
    font-weight: 700;
    color: var(--accent);
    margin-bottom: 30px;
    padding-top: 20px;
  }
`;

export const SummaryLabel = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
  text-transform: uppercase;

  &.total {
    color: var(--accent);
    font-size: 16px;
    font-weight: 700;
  }
`;

export const SummaryValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);

  &.total {
    color: var(--accent);
    font-size: 20px;
    font-weight: 700;
  }

  &.discount {
    color: var(--success);
  }
`;

export const PlaceOrderButton = styled.button`
  width: 100%;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  padding: 20px;
  border-radius: 30px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;

  box-shadow: var(--shadow-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  letter-spacing: 1px;
  margin-bottom: 20px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
    color: var(--surface);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &.processing {
    animation: ${pulse} 1.5s infinite;
  }
`;

export const SecurityInfo = styled.div`
  text-align: center;
  padding: 20px;

  border-radius: 15px;
  border: 2px solid var(--accent-light);
  transition: all 0.3s ease;

  .security-icons {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-bottom: 15px;
    font-size: 24px;
    color: var(--success);
  }

  p {
    font-size: 12px;
    color: var(--text-muted);
    margin: 0;
    line-height: 1.4;
  }
`;

export const ErrorMessage = styled.div`
  background: var(--error);
  color: var(--surface);
  padding: 15px 20px;
  border-radius: 15px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  animation: ${fadeInUp} 0.3s ease;

  .icon {
    font-size: 18px;
  }
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  text-decoration: underline;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  padding: 0;

  &:hover {
    color: var(--accent-dark);
  }
`;

export const EmailVerificationBanner = styled.div`
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const EmailVerificationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #b8860b;
`;

export const EmailVerificationText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #b8860b;
`;

export const EmailVerificationButton = styled.button`
  background-color: #ffc107;
  color: #000;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  transition: all 0.3s ease;
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const AutoFilledDataBanner = styled.div`
  background-color: var(--primary-light-bg);
  border: 1px solid var(--primary);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--primary);

  a {
    color: var(--primary);
    text-decoration: underline;
  }
`;

export const LoginPromptBanner = styled.div`
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #b8860b;
`;

export const BackButton = styled.button`
  background: transparent;
  border: 2px solid var(--accent);
  color: var(--accent);
  padding: 15px;
  border-radius: 25px;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;
  text-transform: uppercase;
  font-family: inherit;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    transform: translateY(-2px);
    box-shadow: var(--shadow-soft);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
